import { createSlice, createAsyncThunk, PayloadAction, isRejectedWithValue } from '@reduxjs/toolkit';
import api from '../api';

// Define proper types
interface BlogPost {
  id: string;
  _id?: string;
  id_practice: string;
  title: string;
  description?: string;
  excerpt?: string;
  content: string;
  status: 'draft' | 'published' | 'pending_review' | 'rejected';
  review_results: string;
  rejection_reason?: string | null;
  slug: string;
  deleted: boolean;
  author: string;
  coverImage?: string;
  readTime?: number;
  tags?: string[];
  tag?: string[];
  createdAt?: string | null;
  publishedAt?: string | null;
  created_at: number | null;
  last_modified_at: number | null;
  published_at: number | null;
  rejected_at: number | null;
}

interface BlogState {
  posts: BlogPost[];
  loading: boolean;
  post: BlogPost | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: BlogState = {
  posts: [],
  loading: false,
  post: null,
  status: 'idle',
  error: null,
};

// Async thunks with better error handling
export const fetchPosts = createAsyncThunk<BlogPost[], string | undefined>(
  'blog/fetchPosts',
  async (queryString, { rejectWithValue }) => {
    try {
      const url = queryString && queryString.length > 0 ? `/blog?${queryString}` : '/blog';
      const response = await api.get(url);
      return response.data.body as BlogPost[];
    } catch (error: any) {
      console.error('Error fetching posts:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch posts'
      );
    }
  }
);

export const fetchPublishedBlogs = createAsyncThunk<BlogPost[]>(
  'blog/fetchPublishedBlogs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/blog/published');
      return response.data.body as BlogPost[];
    } catch (error: any) {
      console.error('Error fetching published posts:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch published posts'
      );
    }
  }
)

export const fetchBlogById = createAsyncThunk(
  'blog/fetchBlogById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/blog/${id}`);
      return response.data.body;
    } catch (error: any) {
      console.error('Error fetching blog by ID:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch blog post'
      );
    }
  }
);

export const fetchBlogBySlug = createAsyncThunk(
  'blog/fetchBlogBySlug',
  async (slug: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/blog/slug/${slug}`);
      return response.data.body as BlogPost;
    } catch (error: any) {
      console.error('Error fetching blog by slug:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch blog post'
      );
    }
  }
);

export const updateBlog = createAsyncThunk(
  'blog/updateBlog',
  async ({ id, blogData }: { id: string; blogData: Partial<BlogPost> }, { rejectWithValue }) => {

    const blogDataToSend = {
      ...blogData,
      id: id,
    }
    
    try {
      const response = await api.patch(`/blog/${id}`, blogDataToSend);
      return response.data.body as BlogPost;
    } catch (error: any) {
      console.error('Error updating blog:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update blog post'
      );
    }
  }
);

export const createBlog = createAsyncThunk(
  'blog/createBlog',
  async (blogData: BlogPost, { rejectWithValue }) => {
    // Remove _id if it exists
    if ('_id' in blogData) {
      delete (blogData as any)['_id'];
    }
    try {
      const response = await api.post('/blog', blogData);
      // Some endpoints wrap the record in `body`, others return it directly.
      // Fall back to the request payload so callers never receive undefined.
      return (response.data?.body ?? response.data ?? blogData) as BlogPost;

    } catch (error: any) {
      console.error('Error creating blog:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create blog post'
      );
    }
  }
);

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    clearCurrentPost: (state) => {
      state.post = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    // Fetch all posts
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action: PayloadAction<BlogPost[]>) => {
        state.status = 'succeeded';
        // `posts` must always be an array; a body-less response would otherwise
        // make it undefined and crash the next render that maps over it.
        state.posts = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch posts';
      })
      
      // Fetch blog by ID
      .addCase(fetchBlogById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBlogById.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        state.status = 'succeeded';
        state.post = action.payload;
        state.error = null;
      })
      .addCase(fetchBlogById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch blog post';
      })
      
      // Fetch blog by slug
      .addCase(fetchBlogBySlug.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBlogBySlug.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        state.status = 'succeeded';
        state.post = action.payload;
        state.error = null;
      })
      .addCase(fetchBlogBySlug.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch blog post';
      })
      
      // Update blog
      .addCase(updateBlog.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateBlog.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        state.status = 'succeeded';
        state.post = action.payload;
        
        // Update the post in the posts array if it exists
        const index = state.posts.findIndex(post => post.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
        
        state.error = null;
      })
      .addCase(updateBlog.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to update blog post';
      })
      
      // Create blog
      .addCase(createBlog.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createBlog.fulfilled, (state, action: PayloadAction<BlogPost>) => {
        state.status = 'succeeded';
        // A create response that echoes nothing back must not put a hole in the
        // list — consumers map over `posts` and would crash on an undefined entry.
        if (action.payload && typeof action.payload === 'object') {
          state.posts.push(action.payload);
        }
        state.error = null;
      })
      .addCase(createBlog.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to create blog post';
      })
      
      // Fetch published blogs
      .addCase(fetchPublishedBlogs.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPublishedBlogs.fulfilled, (state, action: PayloadAction<BlogPost[]>) => {
        state.status = 'succeeded';
        state.posts = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(fetchPublishedBlogs.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch published posts';
      })
  },
});

export const { clearCurrentPost, clearError, resetStatus } = blogSlice.actions;

// Selectors for easy state access
export const selectAllPosts = (state: { blog: BlogState }) => state.blog.posts;
export const selectCurrentPost = (state: { blog: BlogState }) => state.blog.post;
export const selectBlogStatus = (state: { blog: BlogState }) => state.blog.status;
export const selectBlogError = (state: { blog: BlogState }) => state.blog.error;
export const selectIsLoading = (state: { blog: BlogState }) => state.blog.status === 'loading';

export default blogSlice.reducer;
